import {
  type FullAssessmentSectionId,
  type FullAssessmentSectionResult,
  FullAssessmentSectionStatus,
} from '@azymmetric/shared/types/full-assessment.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Avatar } from '@components/shared/avatar.component';
import { BlueLabel } from '@components/shared/blue-label.component';
import { EffectDefinitionTooltip } from '@components/shared/effect-definition.component';
import { Spinner } from '@components/shared/spinner.component';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@components/ui/accordion';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { cn } from '@lib/utils';
import { useGetCapabilities } from '@queries/capability.query';
import { useGetCategories } from '@queries/category.query';
import { useGetCommunities } from '@queries/community.query';
import { useGetDigitalIntegrations } from '@queries/digital-integration.query';
import { useGetDomains } from '@queries/domain.query';
import { useGetEffects } from '@queries/effect.query';
import { useGetKillChains } from '@queries/kill-chain.query';
import { useGetOperationalIntegrations } from '@queries/operational-integration.query';
import { useGetPhysicalIntegrations } from '@queries/physical-integration.query';
import { useGetServices } from '@queries/service.query';
import { useGetTargetSystems } from '@queries/target-system.query';
import { useGetUses } from '@queries/use.query';
import type { MissionAssessmentFormData } from '@schemas/mission-assessment.schema';
import { countries } from '@utils/constants';
import { CheckCheckIcon, CircleHelpIcon, XIcon } from 'lucide-react';
import { type FC, type ReactNode, useMemo, useState } from 'react';
import { FullAssessmentSectionCard } from './full-assessment-section-card.component';
import {
  type FullAssessmentSectionPatch,
  type FullAssessmentSectionStates,
  buildSeedPatches,
  isSectionAcceptable,
  isUnresolvedDraft,
  orderedSectionIds,
  parseSectionPatch,
  sectionPatchesEqual,
} from './full-assessment.util';

type CatalogKind =
  | 'capability'
  | 'category'
  | 'community'
  | 'digitalIntegration'
  | 'domain'
  | 'effect'
  | 'killChain'
  | 'operationalIntegration'
  | 'physicalIntegration'
  | 'subcategory'
  | 'targetSystem'
  | 'use';

type NameChip = {
  definition?: string;
  id: string;
  name: string;
};

type ServiceDisplay = {
  imageUrl?: string;
  label: string;
  slug?: string;
};

type EffectPatchValue = Extract<FullAssessmentSectionPatch, { kind: 'effects' }>['value'][number];
type InterPatchValue = Extract<FullAssessmentSectionPatch, { kind: 'inters' }>['value'][number];

type FullAssessmentSectionsPanelProps = {
  acceptedLogIds: Set<string>;
  /**
   * Generation in flight: Accept/Reject All stay disabled and the footer shows
   * a loading status until every section settles.
   */
  isBusy: boolean;
  /**
   * Accept one section into the form; returns whether the patch applied.
   */
  onAcceptSection: (sectionId: FullAssessmentSectionId) => boolean;
  /**
   * Accept every valid non-accepted draft; returns the accepted section ids.
   */
  onAcceptAll: () => FullAssessmentSectionId[];
  onAddComment: (comment: { quote: string; sectionId: FullAssessmentSectionId; text: string }) => void;
  /**
   * Reject every valid non-resolved draft; returns the rejected section ids.
   * The cards fall back to their pre-draft state.
   */
  onRejectAll: () => FullAssessmentSectionId[];
  /**
   * Reject one section's draft; returns whether the rejection applied.
   */
  onRejectSection: (sectionId: FullAssessmentSectionId) => boolean;
  onRetrySection: (sectionId: FullAssessmentSectionId) => void;
  /**
   * Sections whose Retry is queued behind the in-flight generation.
   */
  queuedRetrySections: FullAssessmentSectionId[];
  rejectedLogIds: Set<string>;
  sectionStates: FullAssessmentSectionStates;
  /**
   * Form values captured when the workspace opened: rendered as each section's
   * seed ("current form content") until a generated draft supersedes it.
   * See docs/iterations/AZ-393/AZ-393.DECISIONS.md (D6).
   */
  seedValues: MissionAssessmentFormData;
};

// Chips with a catalog definition show it on hover (the badge itself is the
// tooltip trigger), mirroring the definition tooltips on the assessment form.
const NameChips: FC<{ chips: NameChip[] }> = (props) => {
  const { chips } = props;

  return (
    <div className="flex flex-wrap gap-1">
      {chips.map((chip, index) =>
        chip.definition ? (
          <TooltipComponent
            className="text-xs whitespace-pre-wrap font-normal"
            key={`${chip.id}-${index}`}
            side="top"
            trigger={
              <Badge
                className="cursor-help"
                size="xs"
                variant="outline"
              >
                {chip.name}
                <CircleHelpIcon className="size-3 shrink-0 text-gray-400" />
              </Badge>
            }
          >
            {chip.definition}
          </TooltipComponent>
        ) : (
          <Badge
            key={`${chip.id}-${index}`}
            size="xs"
            variant="outline"
          >
            {chip.name}
          </Badge>
        ),
      )}
    </div>
  );
};

// Plain labels inside accordion content (blue labels stay in triggers only).
const LabeledText: FC<{ label: string; text: string }> = (props) => {
  const { label, text } = props;

  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="whitespace-pre-wrap text-xs text-gray-700">{text}</p>
    </div>
  );
};

const DetailItem: FC<{ label: string; value: string }> = (props) => {
  const { label, value } = props;

  return (
    <div className="min-w-0">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-xs text-gray-700">{value}</p>
    </div>
  );
};

const TriggerLabeledValue: FC<{ badge?: ReactNode; label: string; value: string }> = (props) => {
  const { badge, label, value } = props;

  return (
    <span className="flex min-w-0 flex-col items-start">
      <span className="flex min-w-0 items-center gap-1">
        <span className="truncate text-xs font-medium text-gray-800">{value}</span>
        {badge}
      </span>
      <BlueLabel
        className="text-xs"
        text={label}
      />
    </span>
  );
};

const formatEffectMoney = (money: EffectPatchValue['data']['cost']): string =>
  money ? `${money.amount.toLocaleString()} ${money.currency}` : '—';

const formatEffectTime = (time: EffectPatchValue['data']['timeToDeploy']): string =>
  time ? `${time.value} ${time.unit.toLowerCase()}` : '—';

const formatEnumLabel = (value?: string): string => {
  if (!value) return '—';
  const lowered = value.toLowerCase().replace(/_/g, ' ');
  return lowered.charAt(0).toUpperCase() + lowered.slice(1);
};

export const FullAssessmentSectionsPanel: FC<FullAssessmentSectionsPanelProps> = (props) => {
  const {
    acceptedLogIds,
    isBusy,
    onAcceptAll,
    onAcceptSection,
    onAddComment,
    onRejectAll,
    onRejectSection,
    onRetrySection,
    queuedRetrySections,
    rejectedLogIds,
    sectionStates,
    seedValues,
  } = props;

  // Sections start expanded; accepting a section collapses it.
  const [openSections, setOpenSections] = useState<string[]>(() => orderedSectionIds());

  const { data: capabilities } = useGetCapabilities();
  const { data: categories } = useGetCategories();
  const { data: communities } = useGetCommunities();
  const { data: digitalIntegrations } = useGetDigitalIntegrations();
  const { data: domains } = useGetDomains();
  const { data: effects } = useGetEffects();
  const { data: killChains } = useGetKillChains();
  const { data: operationalIntegrations } = useGetOperationalIntegrations();
  const { data: physicalIntegrations } = useGetPhysicalIntegrations();
  const { data: services } = useGetServices();
  const { data: targetSystems } = useGetTargetSystems();
  const { data: uses } = useGetUses();

  const catalogMaps = useMemo(() => {
    const toMap = (items?: Array<{ id: string; name: string }>) =>
      new Map((items ?? []).map((item) => [item.id, item.name]));

    // Categories nest subcategories; flatten the children into their own map.
    const subcategoryMap = new Map<string, string>();
    for (const category of categories ?? []) {
      for (const subcategory of category.subcategories ?? []) {
        subcategoryMap.set(subcategory.id, subcategory.name);
      }
    }

    return {
      capability: toMap(capabilities),
      category: toMap(categories),
      community: toMap(communities),
      digitalIntegration: toMap(digitalIntegrations),
      domain: toMap(domains),
      effect: toMap(effects),
      killChain: toMap(killChains),
      operationalIntegration: toMap(operationalIntegrations),
      physicalIntegration: toMap(physicalIntegrations),
      subcategory: subcategoryMap,
      targetSystem: toMap(targetSystems),
      use: toMap(uses),
    } satisfies Record<CatalogKind, Map<string, string>>;
  }, [
    capabilities,
    categories,
    communities,
    digitalIntegrations,
    domains,
    effects,
    killChains,
    operationalIntegrations,
    physicalIntegrations,
    targetSystems,
    uses,
  ]);

  // Mirrors the main form's service rendering: the services catalog is a tree
  // whose sub-services carry the flag slug; labels are "CC - Sub-service name".
  const serviceDisplayMap = useMemo(() => {
    const map = new Map<string, ServiceDisplay>();

    for (const service of services ?? []) {
      const countryCode = countries.find((country) => country.name === service.name)?.code;
      map.set(service.id, { label: service.name });

      for (const subService of service.subServices ?? []) {
        map.set(subService.id, {
          ...(subService.slug && { imageUrl: `/images/services/${subService.slug}.png`, slug: subService.slug }),
          label: `${countryCode ? `${countryCode} - ` : ''}${subService.name}`,
        });
      }
    }

    return map;
  }, [services]);

  // Only capabilities and effects carry catalog definitions worth surfacing.
  const definitionMaps = useMemo(
    () => ({
      capability: new Map((capabilities ?? []).map((item) => [item.id, item.description])),
      effect: new Map((effects ?? []).map((item) => [item.id, item.description])),
    }),
    [capabilities, effects],
  );

  const resolveName = (catalog: CatalogKind, id: string): string | undefined => catalogMaps[catalog].get(id);

  const resolveNames = (catalog: CatalogKind, ids: string[]): string[] =>
    ids.map((id) => resolveName(catalog, id) ?? 'Unknown').filter(Boolean);

  const resolveChips = (catalog: CatalogKind, ids: string[]): NameChip[] => {
    const definitionMap = catalog === 'capability' || catalog === 'effect' ? definitionMaps[catalog] : undefined;

    return ids.map((id) => {
      const definition = definitionMap?.get(id);
      return {
        ...(definition && { definition }),
        id,
        name: resolveName(catalog, id) ?? 'Unknown',
      };
    });
  };

  const seedPatches = useMemo(() => buildSeedPatches(seedValues), [seedValues]);
  const sectionPatchStates = useMemo(() => {
    return new Map(
      orderedSectionIds().map((sectionId) => {
        const result = sectionStates.results.get(sectionId);
        const draftPatch = result?.status === FullAssessmentSectionStatus.COMPLETED ? parseSectionPatch(result) : null;
        return [
          sectionId,
          {
            draftPatch,
            isAcceptable: isSectionAcceptable(result),
          },
        ];
      }),
    );
  }, [sectionStates.results]);

  const expandSection = (sectionId: FullAssessmentSectionId) => {
    setOpenSections((current) => (current.includes(sectionId) ? current : [...current, sectionId]));
  };

  const collapseSections = (sectionIds: FullAssessmentSectionId[]) => {
    if (sectionIds.length === 0) return;
    setOpenSections((current) => current.filter((openId) => !sectionIds.some((sectionId) => sectionId === openId)));
  };

  const handleAcceptSection = (sectionId: FullAssessmentSectionId) => {
    if (onAcceptSection(sectionId)) {
      collapseSections([sectionId]);
    }
  };

  const handleAcceptAll = () => {
    collapseSections(onAcceptAll());
  };

  // Resolving a section collapses it, whether accepted or rejected.
  const handleRejectSection = (sectionId: FullAssessmentSectionId) => {
    if (onRejectSection(sectionId)) {
      collapseSections([sectionId]);
    }
  };

  const handleRejectAll = () => {
    collapseSections(onRejectAll());
  };

  const renderEffectsContent = (effectItems: EffectPatchValue[]): ReactNode => (
    <Accordion
      className="space-y-1"
      type="multiple"
    >
      {effectItems.map((effect, index) => (
        <AccordionItem
          className="rounded-md border border-gray-200 bg-gray-50 px-2 last:border-b"
          key={`${effect.effectId}-${effect.useId}-${index}`}
          value={`effect-${index}`}
        >
          <AccordionTrigger className="gap-3 py-2 hover:no-underline">
            <span className="grid min-w-0 flex-1 grid-cols-3 gap-2">
              <TriggerLabeledValue
                badge={
                  <EffectDefinitionTooltip
                    definition={definitionMaps.effect.get(effect.effectId)}
                    side="top"
                  />
                }
                label="Effect"
                value={resolveName('effect', effect.effectId) ?? 'Unknown'}
              />
              <TriggerLabeledValue
                label="Use"
                value={resolveName('use', effect.useId) ?? 'Unknown'}
              />
              <TriggerLabeledValue
                label="Domain"
                value={resolveName('domain', effect.domainId) ?? 'Unknown'}
              />
            </span>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            <p className="text-xs text-gray-700">{effect.data.description}</p>

            <div className="grid grid-cols-2 gap-x-3 gap-y-3 sm:grid-cols-3">
              <DetailItem
                label="Unit"
                value={effect.data.unit ?? '—'}
              />
              <DetailItem
                label="Phases"
                value={`${effect.data.minPhase ?? '—'} – ${effect.data.maxPhase ?? '—'}`}
              />
              <DetailItem
                label="Personnel"
                value={effect.data.personnelRequired !== undefined ? String(effect.data.personnelRequired) : '—'}
              />
              <DetailItem
                label="Cost"
                value={formatEffectMoney(effect.data.cost)}
              />
              <DetailItem
                label="Cost to Replace"
                value={formatEffectMoney(effect.data.costToReplace)}
              />
              <DetailItem
                label="Time to Deploy"
                value={formatEffectTime(effect.data.timeToDeploy)}
              />
              <DetailItem
                label="Time to Replace"
                value={formatEffectTime(effect.data.timeToReplace)}
              />
            </div>

            {effect.data.measureOfImpact && (
              <LabeledText
                label="Measure of Impact"
                text={effect.data.measureOfImpact}
              />
            )}
            {effect.data.measuresOfPerformance && (
              <LabeledText
                label="Measures of Performance"
                text={effect.data.measuresOfPerformance}
              />
            )}

            {effect.secondaryEffects.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500">Secondary Effects</p>
                <NameChips chips={resolveChips('effect', effect.secondaryEffects)} />
              </div>
            )}
            {effect.targetSystems.length > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500">Target Systems</p>
                <NameChips chips={resolveChips('targetSystem', effect.targetSystems)} />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  const renderIntersContent = (interItems: InterPatchValue[]): ReactNode => (
    <Accordion
      className="space-y-1"
      type="multiple"
    >
      {interItems.map((inter, index) => (
        <AccordionItem
          className="rounded-md border border-gray-200 bg-gray-50 px-2 last:border-b"
          key={`${inter.name}-${index}`}
          value={`inter-${index}`}
        >
          <AccordionTrigger className="gap-3 py-2 hover:no-underline">
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <span className="truncate text-xs font-medium text-gray-800">{inter.name}</span>
              {inter.trl !== undefined && (
                <Badge
                  size="xs"
                  variant="outline"
                >
                  TRL {inter.trl}
                </Badge>
              )}
              {inter.mrl !== undefined && (
                <Badge
                  size="xs"
                  variant="outline"
                >
                  MRL {inter.mrl}
                </Badge>
              )}
            </span>
          </AccordionTrigger>
          <AccordionContent className="space-y-3 pb-3">
            {inter.description && <p className="text-xs text-gray-700">{inter.description}</p>}

            <div className="grid grid-cols-2 gap-x-3 gap-y-3 sm:grid-cols-3">
              <DetailItem
                label="Category"
                value={resolveName('category', inter.categoryId) ?? 'Unknown'}
              />
              <DetailItem
                label="Subcategory"
                value={resolveName('subcategory', inter.subcategoryId) ?? 'Unknown'}
              />
              <DetailItem
                label="Country"
                value={inter.country ?? '—'}
              />
              <DetailItem
                label="Reference"
                value={inter.reference ?? '—'}
              />
              <DetailItem
                label="Fielded Status"
                value={formatEnumLabel(inter.fieldedStatus)}
              />
              <DetailItem
                label="Verification Confidence"
                value={formatEnumLabel(inter.verificationConfidence)}
              />
              <DetailItem
                label="Priority Score"
                value={inter.priorityScore !== undefined ? `${inter.priorityScore} / 100` : '—'}
              />
            </div>

            {inter.notes && (
              <LabeledText
                label="Assessment Notes"
                text={inter.notes}
              />
            )}
            {inter.verificationNotes && (
              <LabeledText
                label="Verification Notes"
                text={inter.verificationNotes}
              />
            )}
            {inter.priorityRationale && (
              <LabeledText
                label="Priority Rationale"
                text={inter.priorityRationale}
              />
            )}

            {(inter.digitalIntegrationIds?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500">Digital Integrations</p>
                <NameChips chips={resolveChips('digitalIntegration', inter.digitalIntegrationIds ?? [])} />
              </div>
            )}
            {(inter.operationalIntegrationIds?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500">Operational Integrations</p>
                <NameChips chips={resolveChips('operationalIntegration', inter.operationalIntegrationIds ?? [])} />
              </div>
            )}
            {(inter.physicalIntegrationIds?.length ?? 0) > 0 && (
              <div className="space-y-1">
                <p className="text-xs font-medium text-gray-500">Physical Integrations</p>
                <NameChips chips={resolveChips('physicalIntegration', inter.physicalIntegrationIds ?? [])} />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  const renderPatchContent = (patch: FullAssessmentSectionPatch): ReactNode => {
    switch (patch.kind) {
      case 'capabilities':
        return <NameChips chips={resolveChips('capability', patch.value)} />;
      case 'communities':
        return <NameChips chips={resolveChips('community', patch.value)} />;
      case 'killChains':
        return <NameChips chips={resolveChips('killChain', patch.value)} />;
      case 'services':
        return (
          <div className="space-y-3">
            {patch.value.map((service, index) => {
              const display = serviceDisplayMap.get(service.serviceId);

              return (
                <div
                  className="flex items-center gap-2"
                  key={`${service.serviceId}-${index}`}
                >
                  <Avatar
                    altText={display?.label ?? 'Service'}
                    imageUrl={display?.imageUrl}
                    initialText={display?.slug ?? display?.label}
                    size="sm"
                  />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800">{display?.label ?? 'Unknown service'}</p>
                    {service.unit && <p className="text-xs text-gray-500">{service.unit}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        );
      case 'techUses':
        return (
          <ul className="list-inside list-disc space-y-0.5 text-xs">
            {patch.value.map((techUse, index) => {
              const useName = resolveName('use', techUse.useId) ?? 'Unknown use';
              const domainNames = resolveNames(
                'domain',
                techUse.domains.map((domain) => domain.domainId),
              );
              return (
                <li key={`${techUse.useId}-${index}`}>
                  {useName}
                  {domainNames.length > 0 ? ` (${domainNames.join(', ')})` : ''}
                </li>
              );
            })}
          </ul>
        );
      case 'tasks':
        return (
          <ul className="list-inside list-disc space-y-0.5 text-xs">
            {patch.value.map((task, index) => (
              <li key={`${task.description}-${index}`}>{task.description}</li>
            ))}
          </ul>
        );
      case 'effects':
        return renderEffectsContent(patch.value);
      case 'inters':
        return renderIntersContent(patch.value);
      case 'phases':
        return (
          <p className="text-xs">
            Min phase {patch.value.minPhase} / Max phase {patch.value.maxPhase}
          </p>
        );
      case 'hurdles': {
        // Targets render even without notes: the form defaults Next TRL/MRL
        // scores, so a score-only state still shows meaningful content.
        const { nextMrl, nextMrlScore, nextTrl, nextTrlScore, supplyChain } = patch.value;
        return (
          <div className="space-y-2">
            {(nextTrl?.trim() || nextTrlScore !== undefined) && (
              <LabeledText
                label={`Next TRL${nextTrlScore !== undefined ? ` (target ${nextTrlScore})` : ''}`}
                text={nextTrl?.trim() || 'No notes.'}
              />
            )}
            {(nextMrl?.trim() || nextMrlScore !== undefined) && (
              <LabeledText
                label={`Next MRL${nextMrlScore !== undefined ? ` (target ${nextMrlScore})` : ''}`}
                text={nextMrl?.trim() || 'No notes.'}
              />
            )}
            {supplyChain && (
              <LabeledText
                label="Supply Chain"
                text={supplyChain}
              />
            )}
          </div>
        );
      }
      case 'expertNote':
      case 'generalNotes':
        return <p className="whitespace-pre-wrap text-xs text-gray-700">{patch.value}</p>;
      default:
        return null;
    }
  };

  const renderDraftContent = (patch: FullAssessmentSectionPatch | null | undefined): ReactNode => {
    if (!patch) {
      return <p className="text-xs text-gray-500">The draft payload could not be displayed.</p>;
    }

    return renderPatchContent(patch);
  };

  const isSectionAccepted = (result?: FullAssessmentSectionResult): boolean =>
    Boolean(result?.logId && acceptedLogIds.has(result.logId));

  const isSectionRejected = (result?: FullAssessmentSectionResult): boolean =>
    Boolean(result?.logId && rejectedLogIds.has(result.logId));

  const acceptableCount = orderedSectionIds().filter((sectionId) => {
    const result = sectionStates.results.get(sectionId);
    return isUnresolvedDraft({
      acceptedLogIds,
      isAcceptable: sectionPatchStates.get(sectionId)?.isAcceptable,
      rejectedLogIds,
      result,
    });
  }).length;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Accordion
        className="min-h-0 flex-1 space-y-2 overflow-y-auto p-1"
        onValueChange={setOpenSections}
        type="multiple"
        value={openSections}
      >
        {orderedSectionIds().map((sectionId) => {
          const result = sectionStates.results.get(sectionId);
          const seedPatch = seedPatches.get(sectionId);
          const draftPatch = sectionPatchStates.get(sectionId)?.draftPatch ?? null;
          const isDraftSameAsCurrent = Boolean(seedPatch && draftPatch && sectionPatchesEqual(seedPatch, draftPatch));

          return (
            <FullAssessmentSectionCard
              draftContent={renderDraftContent(draftPatch)}
              isAccepted={isSectionAccepted(result)}
              isDraftSameAsCurrent={isDraftSameAsCurrent}
              isRejected={isSectionRejected(result)}
              isRetryQueued={queuedRetrySections.includes(sectionId)}
              isStreaming={sectionStates.streamingSections.has(sectionId)}
              key={sectionId}
              onAccept={() => handleAcceptSection(sectionId)}
              onAddComment={(comment) => onAddComment({ ...comment, sectionId })}
              onExpand={() => expandSection(sectionId)}
              onReject={() => handleRejectSection(sectionId)}
              onRetry={() => onRetrySection(sectionId)}
              result={result}
              sectionId={sectionId}
              seedContent={seedPatch ? renderPatchContent(seedPatch) : undefined}
            />
          );
        })}
      </Accordion>

      {/* No bottom padding: the modal's content padding provides the bottom gap,
          matching the chat input's distance to the modal edge. */}
      <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-200 px-3 pt-3">
        <p className="text-xs font-normal text-gray-500">Accepted sections are written into the form</p>

        {/* While generating, the actions can't resolve drafts yet: show a
            loading status in their place. `invisible` (not conditional render)
            keeps the footer height stable when there is nothing to resolve. */}
        {isBusy ? (
          <div className="flex h-8 items-center gap-2 text-xs font-normal text-gray-500">
            <Spinner size="sm" />
            Generating sections…
          </div>
        ) : (
          <div className={cn('flex items-center gap-2', acceptableCount === 0 && 'invisible')}>
            <Button
              disabled={acceptableCount === 0}
              onClick={handleRejectAll}
              size="sm"
              type="button"
              variant="destructive"
            >
              <XIcon size={14} />
              Reject All
            </Button>
            <Button
              disabled={acceptableCount === 0}
              onClick={handleAcceptAll}
              size="sm"
              type="button"
              variant="primary"
            >
              <CheckCheckIcon size={14} />
              Accept All{acceptableCount > 0 ? ` (${acceptableCount})` : ''}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
